import { RefObject, useEffect } from 'react';
import useMatchMedia from 'hooks/useMatchMedia';

type Params = {
  dropdownRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  triggerRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  setIsUpwards: (param: boolean) => void;
  setIsBackwards: (param: boolean) => void;
  renderAsPortal: boolean;
  preferUpwards?: boolean;
};

const usePositioning = ({
  dropdownRef,
  contentRef,
  triggerRef,
  isOpen,
  setIsUpwards,
  setIsBackwards,
  renderAsPortal,
  preferUpwards,
}: Params) => {
  const isTouchDevice = useMatchMedia('isTouchDevice');

  useEffect(() => {
    const getWindowDimensions = () => {
      const screenHeight = isTouchDevice
        ? window.outerHeight
        : window.innerHeight;

      const screenWidth = isTouchDevice ? window.outerWidth : window.innerWidth;

      return {
        screenHeight,
        screenWidth,
      };
    };

    const positionContent = (screenWidth: number, screenHeight: number) => {
      const offset = 10; // px

      const {
        top: dropdownTopOffset,
        left: dropdownLeftOffset,
        right: ddRightOffset,
      } = dropdownRef.current.getBoundingClientRect();

      const { height: contentHeight, width: contentWidth } =
        contentRef.current.getBoundingClientRect();

      const { height: triggerHeight, width: triggerWidth } =
        triggerRef.current.getBoundingClientRect();

      // Content is translated for the trigger's height where the beginning
      // is the trigger top if portal is not used (0), or offset from the
      // top of the page to the trigger(dropdownTopOffset) + scrolledHeight
      // (window.scrollY) if the portal is used.
      // ---offset for upwards:
      let topOffset = !renderAsPortal ? 0 : dropdownTopOffset + window.scrollY;

      const bottomSpace = screenHeight - (dropdownTopOffset + offset);
      const topSpace = dropdownTopOffset;

      let chosenSpace = dropdownTopOffset;
      let maxHeight = 0;

      if (topSpace >= contentHeight + offset && preferUpwards)
        setIsUpwards(true);
      else if (bottomSpace > dropdownTopOffset) {
        setIsUpwards(false);
        topOffset = !renderAsPortal ? triggerHeight : topOffset + triggerHeight;
        chosenSpace = bottomSpace;
      } else {
        setIsUpwards(true);
      }

      if (chosenSpace < contentHeight) {
        // popup top-margin is 20.
        maxHeight = chosenSpace - 40;
      }

      const leftOffset = !renderAsPortal
        ? triggerWidth / 2
        : dropdownLeftOffset + window.scrollX + triggerWidth / 2;

      const rightOffset = !renderAsPortal
        ? triggerWidth / 2
        : screenWidth - (ddRightOffset - window.scrollX - triggerWidth / 2);

      let maxWidth = 0;

      if (leftOffset < contentWidth / 2) {
        maxWidth = leftOffset * 2 - 8;
      }

      if (rightOffset < leftOffset && rightOffset < contentWidth / 2) {
        maxWidth = rightOffset * 2 - 8;
      }

      contentRef.current.style.top = `${topOffset}px`;
      contentRef.current.style.left = `${leftOffset}px`;
      if (maxWidth) contentRef.current.style.maxWidth = `${maxWidth}px`;
      if (maxHeight) {
        const wrapper = contentRef.current.querySelector(
          '.resolve-popup__content__content-wrapper',
        );
        const container = contentRef.current.querySelector(
          '.resolve-popup__content__content-wrapper__container',
        );

        contentRef.current.style.maxHeight = `${maxHeight}px`;
        (wrapper as HTMLDivElement).style.maxHeight = `${maxHeight - 10}px`;
        (container as HTMLDivElement).style.maxHeight = `${maxHeight - 50}px`;
      }
      // Added to avoid seeing content movement while calculating the position.
      contentRef.current.style.visibility = 'visible';
    };

    if (isOpen) {
      const { screenHeight, screenWidth } = getWindowDimensions();

      positionContent(screenWidth, screenHeight);

      if (renderAsPortal) {
        const windowScrollCallback = () =>
          positionContent(screenWidth, screenHeight);

        window.addEventListener('scroll', windowScrollCallback);

        return () => {
          window.removeEventListener('scroll', windowScrollCallback);
        };
      }
    } else {
      if (contentRef.current) contentRef.current.style.visibility = 'hidden';
    }
  }, [
    isOpen,
    dropdownRef,
    contentRef,
    triggerRef,
    setIsUpwards,
    setIsBackwards,
    renderAsPortal,
    preferUpwards,
    isTouchDevice,
  ]);
};

export default usePositioning;
